import { computed, ref } from 'vue'
import type {
  CaseloadFull,
  EmbedGoodbye,
  EmbedHello,
  EmbedReady,
  EmbedSessionReady,
  PatientLinkCreate,
  PatientLinkCreated,
  PatientLinkError,
  PatientLinkRemove,
  PatientLinkRemoved,
  PortalDialogKind,
  PortalDialogOpen,
  PortalNavigate,
  PortalNavigateTarget,
  RouteChanged,
  SessionRefresh,
} from '@/types/lingotalk-bridge-protocol'
import { setTherapistToken, clearTherapistToken } from '@/lib/auth-token'
import { useActivePatientStore } from '@/stores/activePatient'
import {
  trackEmbedHandshakeRecovered,
  trackEmbedHandshakeStalled,
} from '@/lib/telemetry-events-therapist'

// Baked in at build time — never derive from runtime config so the strict
// equality check in the message listener cannot be bypassed.
const PORTAL_ORIGIN: string = (import.meta.env.VITE_PORTAL_EMBED_ORIGIN as string) ?? ''

export interface BridgePatient {
  portalPatientId: string
  ident: string
  name: string
  archived: boolean
  /**
   * Background colour of the patient's avatar — a hex from the portal's
   * 28-entry palette, e.g. "#2E7447" — or null when the portal hasn't assigned
   * one yet.
   */
  avatarColor?: string | null
  /**
   * Initials to draw on that colour, e.g. "MA". Derived by the portal from the
   * decrypted name and sent ready-made, so we never re-derive them; null while
   * the portal's vault is locked.
   *
   * Both optional so a portal build predating the avatar system still satisfies
   * the type — consumers must treat missing and null alike (see
   * PatientAvatar.vue's placeholder).
   */
  initials?: string | null
  /**
   * The LingoTalk patient this portal patient is — or *was* — paired with.
   * Survives the pairing ending: it is how the practice reaches the history it
   * is still entitled to, up to the split.
   */
  linkedLingotalkPatientId: string | null
  /**
   * Whether that pairing is still live. Normalized on arrival (never optional
   * here), so consumers can read it directly: anything that writes — assigning
   * an exercise, setting homework — must gate on this rather than on the id,
   * which outlives the pairing by design.
   */
  lingotalkLinkActive: boolean
}

export type BridgeConnectionState =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'vault_locked'
  // The portal has parked us on purpose: it is waiting for the therapist to
  // answer something, not for the network. Distinct from 'idle' because we must
  // NOT keep announcing, and distinct from 'error' because nothing has failed.
  | 'waiting_on_portal'
  | 'error'
  | 'goodbye'

// ── Singleton state ─────────────────────────────────────────────────────────
// The embed bridge is a single, app-lifetime instance — NOT per-component.
// The portal hands off the session once (handshake) and keeps it alive via the
// MessageChannel port (session:refresh, caseload updates). A therapist spends
// almost all their time on the workspace routes, not /embed, so the bridge must
// outlive any single screen; otherwise the port closes on navigation and the
// Sanctum token can never be refreshed. mount() is therefore called once,
// globally (App.vue), and is idempotent.
const connectionState = ref<BridgeConnectionState>('idle')
const patients = ref<BridgePatient[]>([])
// Flips true once the portal has pushed its first caseload:full for the current
// session. The portal always sends one on port open (even an empty list), so a
// consumer can wait for this before deciding "0 patients" vs "not loaded yet" —
// avoids racing the post-handshake caseload push (ready fires before it lands).
const caseloadReceived = ref<boolean>(false)
// Bumped on every `caseload:full`, identical payload or not — see the handler.
const caseloadVersion = ref(0)
const errorMessage = ref<string | null>(null)
const therapistId = ref<string>('')
const practiceId = ref<string>('')
const linkingPatientId = ref<string | null>(null)
// Deep-link restore target supplied by the portal on the embed:hello handshake.
// App.vue watches this and navigates the iframe there once the session is ready;
// null means "no restore — keep the default landing". See notifyRoute() for the
// outbound half of the round-trip.
const initialRoute = ref<string | null>(null)

// True once the portal's caseload holds at least one non-archived patient — i.e.
// the therapist has a patient tablet account connected to work with. Mirrors
// PatientPicker's `selectablePatients` filter so "can I pick a patient?" and
// "should the patient / homework affordances show?" stay in lockstep. Empty until
// the first caseload:full lands (see caseloadReceived to tell "none" from "not
// loaded yet").
const hasConnectedPatients = computed(() => patients.value.some((p) => !p.archived))

let port: MessagePort | null = null
// currentSessionToken: the Sanctum token issued by POST /api/therapist/session.
// Replaces the JWT for all subsequent API calls so the JWT remains single-use.
let currentSessionToken = ''
// Guards against double-mounting (App.vue and a belt-and-suspenders caller).
let started = false
// Handshake liveness. The portal attaches its embed:ready listener only after
// an awaited network call (syncLinks), so a single fire-once embed:ready can
// land before it's listening and be dropped — postMessage doesn't buffer. We
// re-announce on an interval until the portal answers (state leaves 'idle'),
// then a watchdog converts a never-answered handshake into a recoverable error
// instead of an infinite "Verbinde mit Portal …" spinner.
const ANNOUNCE_INTERVAL_MS = 400
const HANDSHAKE_WATCHDOG_MS = 12_000
let announceTimer: ReturnType<typeof setInterval> | null = null
let watchdogTimer: ReturnType<typeof setTimeout> | null = null
let announceAttempts = 0
// Last route path sent via route:changed; dedupes redundant sends when several
// programmatic navigations resolve to the same path (e.g. guard redirects).
let lastSentPath: string | null = null

// ── Helpers ─────────────────────────────────────────────────────────────────

function makeEnvelope<T extends { type: string }>(msg: Omit<T, 'sentAt' | 'protocolVersion'>): T {
  return { ...msg, sentAt: Date.now(), protocolVersion: 1 } as unknown as T
}

function sendViaPort(msg: object): void {
  port?.postMessage(msg)
}

// Exchange the handoff JWT for a Sanctum embed-session token.
// The JWT is used exactly once (JTI enforced server-side). Returns the
// plain-text Sanctum token on success, or null on failure.
async function exchangeJwt(jwt: string): Promise<string | null> {
  try {
    const res = await fetch('/api/therapist/session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ jwt }),
    })
    if (!res.ok) return null
    const data = (await res.json()) as { access_token: string }
    return data.access_token
  } catch {
    return null
  }
}

// ── Port message handler ──────────────────────────────────────────────────────

function handlePortMessage(event: MessageEvent): void {
  const data: unknown = event.data
  if (typeof data !== 'object' || data === null) return
  const msg = data as Record<string, unknown>
  if (typeof msg['type'] !== 'string') return
  if (msg['protocolVersion'] !== 1) return

  switch (msg['type']) {
    case 'caseload:full': {
      const { payload } = msg as unknown as CaseloadFull
      // Defensive: a malformed payload (missing/!array patients) must not crash
      // the consumers that call `patients.value.filter(...)`.
      // One place for the `lingotalkLinkActive` fallback, so no consumer has to
      // know that an older portal omits it: a patient the portal told us about
      // with an id at all was, before the field existed, by definition paired.
      patients.value = Array.isArray(payload?.patients)
        ? payload.patients.map((p) => ({
            ...p,
            lingotalkLinkActive: p.lingotalkLinkActive ?? p.linkedLingotalkPatientId !== null,
          }))
        : []
      caseloadReceived.value = true
      // Counted, not just assigned: after a pairing dialog the portal re-pushes
      // a caseload that can be byte-identical to the last one (the stub's link
      // row was already active), and a screen watching for "something about
      // this patient may have changed" needs an edge to watch.
      caseloadVersion.value += 1
      // The portal only sends a caseload with the vault unlocked, so its arrival
      // is itself proof of unlock. Recover here even if the explicit
      // `vault:unlocked` was never sent or raced a re-handshake (which would
      // leave the `vault:unlocked` guard below dropping it) — otherwise the host
      // wedges in `vault_locked` forever with a fully-loaded caseload and no
      // safety net can fire (they all gate on `ready`).
      if (connectionState.value === 'vault_locked') connectionState.value = 'ready'
      break
    }

    case 'session:refresh': {
      const { payload } = msg as unknown as SessionRefresh
      // Exchange fresh JWT for a new Sanctum token; non-fatal if it fails
      // (LingoTalk will eventually lose its session, which is intentional).
      void exchangeJwt(payload.handoffJwt).then((token) => {
        if (token) {
          currentSessionToken = token
          // Persist so the shared `api` client (and any view) can authenticate.
          setTherapistToken(token)
        }
      })
      break
    }

    case 'vault:locked':
      // A locked vault means the portal withholds the caseload, and the
      // decrypted names must not linger — neither in memory nor on any
      // workspace screen still rendering them. Drop them and reset to the
      // "not loaded yet" state so a later vault:unlocked + fresh caseload:full
      // repopulates from scratch (consumers tell "loading" from "empty" via
      // caseloadReceived). The portal never locks in v1; this keeps the
      // forwards-compat hook honest for when it does.
      patients.value = []
      caseloadReceived.value = false
      connectionState.value = 'vault_locked'
      break

    case 'vault:unlocked':
      if (connectionState.value === 'vault_locked') connectionState.value = 'ready'
      break

    case 'embed:goodbye': {
      const { payload } = msg as unknown as EmbedGoodbye
      void payload // reason available for logging if needed
      patients.value = []
      caseloadReceived.value = false
      currentSessionToken = ''
      clearTherapistToken()
      // Drop the locked-in patient so a subsequent session in this same SPA
      // instance starts at the chooser, never on a stale patient's screens.
      useActivePatientStore().clear()
      port?.close()
      port = null
      initialRoute.value = null
      lastSentPath = null
      connectionState.value = 'goodbye'
      break
    }

    case 'patient:link:created': {
      const { payload } = msg as unknown as PatientLinkCreated
      const p = patients.value.find((x) => x.portalPatientId === payload.portalPatientId)
      if (p) {
        p.linkedLingotalkPatientId = payload.lingotalkPatientId
        p.lingotalkLinkActive = true
      }
      break
    }

    case 'patient:link:removed': {
      const { payload } = msg as unknown as PatientLinkRemoved
      const p = patients.value.find((x) => x.portalPatientId === payload.portalPatientId)
      // Null, not "kept but inactive": this message answers our own
      // patient:link:remove, which drops the portal's bookkeeping row outright
      // rather than ending a pairing. (Ending one for real is the Apps tab's
      // "Verknüpfung lösen", which reaches LingoTalk directly.)
      if (p) {
        p.linkedLingotalkPatientId = null
        p.lingotalkLinkActive = false
      }
      break
    }

    case 'patient:link:error': {
      const { payload } = msg as unknown as PatientLinkError
      errorMessage.value = payload.message
      break
    }
  }
}

// ── Window message handler (embed:hello) ──────────────────────────────────────

async function handleWindowMessage(event: MessageEvent): Promise<void> {
  // §6.1 rule 1: strict origin equality is the very first check
  if (!PORTAL_ORIGIN || event.origin !== PORTAL_ORIGIN) return

  const data: unknown = event.data
  if (typeof data !== 'object' || data === null) return
  const msg = data as Record<string, unknown>
  if (msg['protocolVersion'] !== 1) return

  // The portal is waiting on the therapist - typically the licence is open on
  // another device and it is asking whether to take it over. Stop announcing
  // and disarm the watchdog: re-announcing behind their dialog produced a
  // refused licence claim every 400ms and then a bogus connection timeout.
  if (msg['type'] === 'embed:hold') {
    stopAnnounce()
    clearWatchdog()
    connectionState.value = 'waiting_on_portal'

    return
  }

  if (msg['type'] !== 'embed:hello') return

  const transferredPort = event.ports[0]
  if (!transferredPort) return

  const { payload } = msg as unknown as EmbedHello
  // The portal answered — stop re-announcing and disarm the stall watchdog
  // before the awaited JWT exchange, so no further embed:ready can race out.
  stopAnnounce()
  clearWatchdog()
  // More than one announce means the first landed before the portal was
  // listening: the self-heal just rescued a session that used to hang.
  if (announceAttempts > 1) {
    console.info(`[embed] embed:hello received after ${announceAttempts} announces (self-healed)`)
    trackEmbedHandshakeRecovered(announceAttempts)
  }
  connectionState.value = 'connecting'
  // A fresh handshake supersedes any prior caseload; wait for the new push.
  caseloadReceived.value = false

  const sessionToken = await exchangeJwt(payload.handoffJwt)
  if (!sessionToken) {
    connectionState.value = 'error'
    errorMessage.value = 'JWT-Validierung fehlgeschlagen'
    console.warn(
      '[embed] JWT exchange failed — POST /api/therapist/session rejected or unreachable',
    )
    return
  }

  currentSessionToken = sessionToken
  // Persist so the shared `api` client (and any view) can authenticate.
  setTherapistToken(sessionToken)
  therapistId.value = payload.therapistId
  practiceId.value = payload.practiceId
  // Deep-link restore target (optional). App.vue reads this once the state
  // flips to 'ready' and navigates there; validation happens there, not here.
  initialRoute.value = typeof payload.initialRoute === 'string' ? payload.initialRoute : null

  // Replace any prior port (a re-handshake supersedes the old channel).
  port?.close()
  port = transferredPort
  port.start()
  port.addEventListener('message', handlePortMessage)

  sendViaPort(makeEnvelope<EmbedSessionReady>({ type: 'embed:session:ready', payload: {} }))
  connectionState.value = 'ready'
}

// ── Link mutations ────────────────────────────────────────────────────────────

// Returns the lingotalk patient id on success (so callers that need to act on
// the freshly-linked patient immediately — e.g. assigning an exercise — don't
// have to wait for the portal's patient:link:created echo to populate
// linkedLingotalkPatientId), or null on failure / when the port isn't ready.
async function createLink(portalPatientId: string): Promise<string | null> {
  if (!port || linkingPatientId.value) return null
  linkingPatientId.value = portalPatientId

  let lingotalkPatientId: string
  try {
    const res = await fetch('/api/therapist/patients', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${currentSessionToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ portal_patient_id: portalPatientId }),
    })
    if (!res.ok) {
      errorMessage.value = `Patienten-Erstellung fehlgeschlagen (${res.status})`
      return null
    }
    const data = (await res.json()) as { lingotalk_patient_id: string }
    lingotalkPatientId = data.lingotalk_patient_id
  } catch {
    errorMessage.value = 'Netzwerkfehler beim Erstellen des Patienten'
    return null
  } finally {
    linkingPatientId.value = null
  }

  sendViaPort(
    makeEnvelope<PatientLinkCreate>({
      type: 'patient:link:create',
      requestId: crypto.randomUUID(),
      payload: { portalPatientId, lingotalkPatientId },
    }),
  )

  return lingotalkPatientId
}

async function removeLink(portalPatientId: string): Promise<void> {
  if (!port || linkingPatientId.value) return
  linkingPatientId.value = portalPatientId
  try {
    sendViaPort(
      makeEnvelope<PatientLinkRemove>({
        type: 'patient:link:remove',
        requestId: crypto.randomUUID(),
        payload: { portalPatientId },
      }),
    )
  } finally {
    linkingPatientId.value = null
  }
}

// ── Portal navigation ─────────────────────────────────────────────────────────

// Ask the portal to navigate its own shell to one of its screens — the patient
// chooser's "Patient:in anlegen" button (patients are created in the portal,
// never here) and the digola mark's "back to the portal". The portal navigating
// away tears this iframe down, so there's no reply to wait for and nothing to
// clean up locally.
//
// Gated on the channel, not on 'ready': every state that has a port ('ready',
// 'vault_locked') is one where the portal can navigate itself, and a therapist
// asking to leave must never be swallowed because the vault happens to be
// locked. The portal resolves the target through its own allow-list, so an
// early or unexpected request degrades to a no-op there, never to anything
// arbitrary. Returns whether the request went out, so a caller can fall back
// when there is no channel at all (standalone, before the handshake, after a
// goodbye).
function requestPortalNavigation(target: PortalNavigateTarget): boolean {
  if (!port) return false
  sendViaPort(makeEnvelope<PortalNavigate>({ type: 'portal:navigate', payload: { target } }))
  return true
}

// Ask the portal to open one of its own dialogs over this iframe — the pairing
// code / unpair confirm for a patient, from the Pro app's patient settings. The
// iframe stays up; the outcome arrives as a fresh `caseload:full` (see
// `caseloadVersion`), not as a reply. Same readiness guard as navigation: with
// no portal listening (standalone dev, a dock) this is a silent no-op.
function requestPortalDialog(dialog: PortalDialogKind, portalPatientId: string): void {
  if (!port || connectionState.value !== 'ready') return
  sendViaPort(
    makeEnvelope<PortalDialogOpen>({
      type: 'portal:dialog:open',
      payload: { dialog, portalPatientId },
    }),
  )
}

// ── Route reporting (deep linking) ─────────────────────────────────────────────

// Report the iframe's current in-app route to the portal so it can mirror it
// into its own address bar (and replay it as initialRoute on the next load).
// No-op until the port is open and the handshake is 'ready' — before that the
// portal has nowhere to put the route and we'd be sending into a closed channel.
// Deduped on path so repeated navigations to the same place send only once.
function notifyRoute(path: string): void {
  if (!port || connectionState.value !== 'ready') return
  if (path === lastSentPath) return
  lastSentPath = path
  sendViaPort(makeEnvelope<RouteChanged>({ type: 'route:changed', payload: { path } }))
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

// Announce readiness to the portal parent frame. Counted so the watchdog and
// the recovery signal can report how many tries it took.
function postReady(): void {
  announceAttempts += 1
  window.parent.postMessage(
    makeEnvelope<EmbedReady>({
      type: 'embed:ready',
      payload: {
        lingotalkBuildId: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown',
      },
    }),
    PORTAL_ORIGIN,
  )
}

function stopAnnounce(): void {
  if (announceTimer !== null) {
    clearInterval(announceTimer)
    announceTimer = null
  }
}

function clearWatchdog(): void {
  if (watchdogTimer !== null) {
    clearTimeout(watchdogTimer)
    watchdogTimer = null
  }
}

// Begin (or restart) the embed:ready handshake. Announce immediately, then keep
// re-announcing every ANNOUNCE_INTERVAL_MS while still unacknowledged ('idle'),
// so an announce dropped before the portal's listener attached gets retried.
// handleWindowMessage stops the interval on the first embed:hello; the watchdog
// caps it so a portal that never answers surfaces an error (not a dead spinner).
function startAnnounce(): void {
  stopAnnounce()
  clearWatchdog()
  announceAttempts = 0
  postReady()
  announceTimer = setInterval(() => {
    // Acknowledged (or moved on) — nothing left to announce.
    if (connectionState.value !== 'idle') {
      stopAnnounce()
      return
    }
    postReady()
  }, ANNOUNCE_INTERVAL_MS)
  watchdogTimer = setTimeout(() => {
    watchdogTimer = null
    // Only the diagnosed hang: still 'idle' means we never heard back at all. A
    // handshake that advanced (connecting/ready/error/…) owns its own state.
    if (connectionState.value === 'idle') {
      stopAnnounce()
      connectionState.value = 'error'
      errorMessage.value = 'Zeitüberschreitung bei der Verbindung zum Portal'
      console.warn(
        `[embed] handshake timed out after ${announceAttempts} embed:ready announces — no embed:hello from portal`,
      )
      trackEmbedHandshakeStalled(announceAttempts)
    }
  }, HANDSHAKE_WATCHDOG_MS)
}

// Mount once, globally (App.vue), when running inside the portal iframe.
// Idempotent: a second call is a no-op so a defensive caller can't double-bind.
function mount(): void {
  if (started) return
  if (!PORTAL_ORIGIN) {
    connectionState.value = 'error'
    errorMessage.value = 'VITE_PORTAL_EMBED_ORIGIN ist nicht konfiguriert'
    return
  }
  started = true

  window.addEventListener('message', handleWindowMessage)
  startAnnounce()
}

// Re-run the handshake after a stall. Wired to the "Neu verbinden" button the
// embed host shows in the error state. No-op before mount() / without an origin.
function retry(): void {
  if (!started || !PORTAL_ORIGIN) return
  connectionState.value = 'idle'
  errorMessage.value = null
  startAnnounce()
}

// Full teardown of the app-lifetime bridge. Not tied to any component — the
// session must survive navigation — so this only runs on an explicit shutdown.
// A live session normally ends via embed:goodbye (handled above).
function unmount(): void {
  window.removeEventListener('message', handleWindowMessage)
  stopAnnounce()
  clearWatchdog()
  announceAttempts = 0
  port?.close()
  port = null
  started = false
  patients.value = []
  caseloadReceived.value = false
  caseloadVersion.value = 0
  currentSessionToken = ''
  therapistId.value = ''
  practiceId.value = ''
  initialRoute.value = null
  lastSentPath = null
}

export function useEmbedBridge() {
  return {
    connectionState,
    patients,
    hasConnectedPatients,
    caseloadReceived,
    caseloadVersion,
    errorMessage,
    therapistId,
    practiceId,
    linkingPatientId,
    initialRoute,
    mount,
    unmount,
    retry,
    createLink,
    removeLink,
    requestPortalNavigation,
    requestPortalDialog,
    notifyRoute,
  }
}
